export const US_STATE_CODES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

export const US_STATE_CODES_OPTIONS = US_STATE_CODES.map((c) => ({ value: c, label: c }));

export const MARKET_TO_STATE_ABBREV = {
  Asheville: 'NC',
  Atlanta: 'GA',
  Austin: 'TX',
  Bakersfield: 'CA',
  Birmingham: 'AL',
  Boise: 'ID',
  Charlotte: 'NC',
  Chattanooga: 'TN',
  Cincinnati: 'OH',
  Clarksville: 'TN',
  Cleveland: 'OH',
  'Colorado Springs': 'CO',
  Columbia: 'SC',
  Columbus: 'OH',
  'Corpus Christi': 'TX',
  Dallas: 'TX',
  Denver: 'CO',
  'Fort Collins': 'CO',
  Greensboro: 'NC',
  Greenville: 'SC',
  Houston: 'TX',
  Huntsville: 'AL',
  Indianapolis: 'IN',
  Jacksonville: 'TN',
  'Johnson City': 'TN',
  'Kansas City': 'KS',
  Killeen: 'TX',
  Knoxville: 'TN',
  'Las Vegas': 'NV',
  'Los Angeles': 'CA',
  Louisville: 'KY',
  Memphis: 'TN',
  Miami: 'FL',
  Minneapolis: 'MN',
  Nashville: 'TN',
  'New York New Jersey': 'NY',
  'Northern Colorado': 'CO',
  'Oklahoma City': 'OK',
  Orlando: 'FL',
  Phoenix: 'AZ',
  Portland: 'OR',
  Prescott: 'AZ',
  Raleigh: 'NC',
  Reno: 'NV',
  Riverside: 'CA',
  Sacramento: 'CA',
  'Salt Lake City': 'UT',
  'San Antonio': 'TX',
  'San Diego': 'CA',
  'Sf Bay Area': 'CA',
  Springfield: 'MO',
  'St Louis': 'MO',
  Tampa: 'FL',
  Tucson: 'AZ',
  Tulsa: 'OK',
  Unknown: '',
  'Washington Dc': 'D.C.',
  Wichita: 'KS',
};

// This version of the market display name is used in a lot of logic.
// Let's keep this version and add the extended version below.
export const getMarketDisplayName = (market: string) => {
  let splits = (market as string).split('_');
  // check if last element is a state code
  if (
    splits.length > 1 &&
    splits[splits.length - 1].length === 2 &&
    US_STATE_CODES.indexOf(splits[splits.length - 1].toUpperCase()) > 0
  ) {
    splits = splits.slice(0, splits.length - 1);
  }
  return splits.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
};

export const getMarketDisplayNameWithState = (market: string | null) => {
  let splits = (market as string).split('_');
  let stateAbbrev = '';
  // check if last element is a state code, remove it temporarily
  if (
    splits.length > 1 &&
    splits[splits.length - 1].length === 2 &&
    US_STATE_CODES.indexOf(splits[splits.length - 1].toUpperCase()) >= 0
  ) {
    stateAbbrev = splits[splits.length - 1];
    splits = splits.slice(0, splits.length - 1);
  }

  // Generate sentence casing and re-append the state code
  const lowerCaseSplits = splits.map((s) => s.toLowerCase());
  let sentenceCaseMarketName = lowerCaseSplits
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
  // Any markets launched after Q1 2022 will be systematically constructed e.g. Phoenix, AZ
  // The above constant (MARKET_TO_STATE_ABBREV) is a helper for markets created prior to this system,
  // and does not need to be used/updated for any future markets.
  stateAbbrev =
    stateAbbrev === ''
      ? (stateAbbrev = MARKET_TO_STATE_ABBREV[sentenceCaseMarketName])
      : stateAbbrev.toUpperCase();

  // Handle a few edge cases
  sentenceCaseMarketName = handleEdgeCases(sentenceCaseMarketName);

  return sentenceCaseMarketName + ', ' + stateAbbrev;
};

function handleEdgeCases(market: string): string {
  if (market === 'Clarksville Ky') {
    market = 'Clarksville';
  }
  if (market === 'Louisville In') {
    market = 'Louisville';
  }
  if (market === 'New York New Jersey') {
    market = 'New York';
  }
  if (market === 'Sf Bay Area') {
    market = 'San Francisco Bay Area';
  }
  if (market === 'Washington Dc') {
    market = 'Washington';
  }
  return market;
}

export enum Markets {
  ALBUQUERQUE_NM = 'albuquerque_nm',
  ATLANTA = 'atlanta',
  AUSTIN = 'austin',
  BOISE = 'boise',
  CHARLOTTE = 'charlotte',
  COLUMBIA_SC = 'columbia_sc',
  COLUMBUS_OH = 'columbus_oh',
  DALLAS = 'dallas',
  DENVER = 'denver',
  HOUSTON = 'houston',
  JACKSONVILLE = 'jacksonville',
  LAS_VEGAS = 'las_vegas',
  LOS_ANGELES = 'los_angeles',
  MINNEAPOLIS = 'minneapolis',
  NASHVILLE = 'nashville',
  ORLANDO = 'orlando',
  PHOENIX = 'phoenix',
  PORTLAND = 'portland',
  RALEIGH = 'raleigh',
  RIVERSIDE = 'riverside',
  SACRAMENTO = 'sacramento',
  SALT_LAKE_CITY = 'salt_lake_city',
  SAN_ANTONIO = 'san_antonio',
  SAN_DIEGO = 'san_diego',
  ST_LOUIS = 'st_louis',
  TAMPA = 'tampa',
  TUCSON = 'tucson',
}

export const TESTING_MARKETS: string[] = [
  Markets.SAN_ANTONIO,
  Markets.DENVER,
  Markets.COLUMBIA_SC,
  Markets.CHARLOTTE,
  Markets.COLUMBUS_OH,
  Markets.HOUSTON,
  Markets.ALBUQUERQUE_NM,
  Markets.TUCSON,
  Markets.TAMPA,
  Markets.RIVERSIDE,
];

// We have to hard code this until protos are consistent with state abbreviations
export const alphabetize = (a, b): number => {
  return a.label < b.label ? -1 : a.label > b.label ? 1 : 0;
};

// TODO: Drop the use of this constant in favor of using the market data stored in web or PBDB
export const BWOD_MARKETS = [
  { label: 'Phoenix, AZ', value: 'phoenix' },
  { label: 'Los Angeles, CA', value: 'los_angeles' },
  { label: 'San Diego, CA', value: 'san_diego_ca' },
  { label: 'Tucson, AZ', value: 'tucson' },
  { label: 'San Antonio, TX', value: 'san_antonio' },
  { label: 'Houston, TX', value: 'houston' },
  { label: 'Charlotte, NC', value: 'charlotte' },
  { label: 'Sacramento, CA', value: 'sacramento' },
  { label: 'Raleigh-Durham, NC', value: 'raleigh' },
  { label: 'Orlando, FL', value: 'orlando' },
  { label: 'Dallas-Fort Worth, TX', value: 'dallas' },
  { label: 'Atlanta, GA', value: 'atlanta' },
  { label: 'Riverside, CA', value: 'riverside' },
  { label: 'Austin, TX', value: 'austin' },
  { label: 'Tampa, FL', value: 'tampa' },
  { label: 'Jacksonville, FL', value: 'jacksonville' },
  { label: 'Denver, CO', value: 'denver' },
  { label: 'Northern Colorado, CO', value: 'northern_colorado' },
  { label: 'Greensboro, NC', value: 'greensboro_nc' },
  { label: 'Minneapolis-Saint Paul, MN', value: 'minneapolis' },
  { label: 'Nashville, TN', value: 'nashville' },
  { label: 'Las Vegas, NV', value: 'las_vegas' },
  { label: 'Boise, ID', value: 'boise' },
  { label: 'Salt Lake City, UT', value: 'salt_lake_city' },
  { label: 'Columbia, SC', value: 'columbia_sc' },
  { label: 'Indianapolis, IN', value: 'indianapolis_in' },

  // Phase 3
  { label: 'Prescott, AZ', value: 'prescott_az' },
  { label: 'St. Louis, MO', value: 'st_louis' },
  { label: 'Birmingham, AL', value: 'birmingham_al' },
  { label: 'Miami, FL', value: 'miami_fl' },
  { label: 'Greenville, SC', value: 'greenville_sc' },
  { label: 'Oklahoma City, OK', value: 'oklahoma_city_ok' },
  { label: 'Columbus, OH', value: 'columbus_oh' },
  { label: 'Kansas City, KS/MO', value: 'kansas_city' },
  { label: 'Killeen, TX', value: 'killeen_tx' },

  // Phase 4
  { label: 'Chattanooga, GA/TN', value: 'chattanooga' },
  { label: 'Corpus Christi, TX', value: 'corpus_christi_tx' },
  { label: 'Knoxville, TN', value: 'knoxville_tn' },
  { label: 'Portland, OR', value: 'portland' },
  { label: 'Cleveland, OH', value: 'cleveland_oh' },
  { label: 'Colorado Springs, CO', value: 'colorado_springs_co' },
  { label: 'Cincinnati, OH', value: 'cincinnati_oh' },

  // Phase 5
  { label: 'Albuquerque, NM', value: 'albuquerque_nm' },
  { label: 'Charleston, SC', value: 'charleston_sc' },
  { label: 'Detroit, MI', value: 'detroit_mi' },
  { label: 'New York / New Jersey, NY', value: 'new_york_new_jersey' },
  { label: 'Richmond, VA', value: 'richmond_va' },
  { label: 'Southwest, FL', value: 'southwest_fl' },

  // Phase 6
  { label: 'Boston, MA', value: 'boston_ma' },
  { label: 'San Francisco Bay Area, CA', value: 'sf_bay_area' },
  { label: 'Washington, DC', value: 'washington_dc' },
].sort(alphabetize);
